/**
 * UK PAYE tax codes (B-0129). Meanings checked against GOV.UK "Understanding your employees' tax codes: What the letters mean"
 * (gov.uk/employee-tax-codes/letters, read 30 Oct 2026): L, M, N, T, 0T, BR, D0, D1, NT, K; S prefix = Scottish bands,
 * C prefix = Welsh bands (the same rates as England); SBR, SD0, SD1, SD2 and SD3 are the Scottish basic, intermediate, higher,
 * advanced and top rates. The number times 10 is the allowance (1257L = 12,570); for a K code that amount is ADDED to income.
 * Not modelled: emergency suffixes (W1, M1, X are accepted and ignored), the rule that deductions cannot exceed half of pay, and
 * the +9 that payroll tables add to the number (the annual figure is the number times 10, as on the code itself).
 */
export type CodeKind = "standard" | "k" | "zero" | "flat" | "nt";
export type CodeRegion = "default" | "scotland" | "wales";

export interface ParsedTaxCode {
  raw: string;
  valid: boolean;
  error?: string;
  kind: CodeKind;
  region: CodeRegion;
  /** Annual allowance in pounds: positive for L, M, N, T; 0 for 0T and flat-rate codes; negative for K codes (added to income). */
  allowance: number;
  /** For flat-rate codes (BR, D0, D1 and the Scottish ones): the rate in percent charged on all taxable pay. */
  flatRate?: number;
  /** For flat-rate codes: the label of the rate. */
  flatLabel?: string;
  letter?: string;
  description: string;
  notes: string[];
}

const NONE: ParsedTaxCode = { raw: "", valid: true, kind: "standard", region: "default", allowance: 0, description: "", notes: [] };

/** Scottish flat-rate codes: GOV.UK names the rate; the percentages are the 2026/27 Scottish rates held in uk.ts (see test). */
export const SCOTTISH_FLAT: Record<string, { rate: number; label: string }> = {
  BR: { rate: 20, label: "Scottish basic rate" }, D0: { rate: 21, label: "Scottish intermediate rate" }, D1: { rate: 42, label: "Scottish higher rate" },
  D2: { rate: 45, label: "Scottish advanced rate" }, D3: { rate: 48, label: "Scottish top rate" },
};
const REST_FLAT: Record<string, { rate: number; label: string }> = { BR: { rate: 20, label: "basic rate" }, D0: { rate: 40, label: "higher rate" }, D1: { rate: 45, label: "additional rate" } };

/** Reads a tax code the way a payslip prints it ("1257L", "BR", "S1257L", "K475", "C1257L", "1257L W1"). Empty input is "no code". */
export function parseTaxCode(input: string): ParsedTaxCode {
  const raw = (input ?? "").trim();
  if (raw === "") return { ...NONE };
  const bad = (error: string): ParsedTaxCode => ({ raw, valid: false, error, kind: "standard", region: "default", allowance: 0, description: "", notes: [] });
  let code = raw.toUpperCase().replace(/\s+/g, "");
  const notes: string[] = [];
  const emergency = /(W1|M1|X)$/.exec(code);
  if (emergency && code.length > emergency[0].length) { code = code.slice(0, -emergency[0].length); notes.push(`The ${emergency[0]} (non-cumulative) marker is ignored: this estimate is for the whole year.`); }
  let region: CodeRegion = "default";
  if (/^S/.test(code)) { region = "scotland"; code = code.slice(1); } else if (/^C/.test(code)) { region = "wales"; code = code.slice(1); }
  const withNotes = (p: Omit<ParsedTaxCode, "raw" | "valid" | "notes">): ParsedTaxCode => ({ raw, valid: true, notes, ...p });
  const regionName = region === "scotland" ? "Scottish" : region === "wales" ? "Welsh" : "";
  if (code === "NT") return withNotes({ kind: "nt", region, allowance: 0, description: "NT: no tax is deducted" });
  if (code === "0T") return withNotes({ kind: "zero", region, allowance: 0, letter: "T", description: `${regionName ? regionName + " " : ""}0T: no personal allowance, all pay taxed through the bands` });
  const flatKey = /^(BR|D[0-3])$/.exec(code)?.[1];
  if (flatKey) {
    if (region === "scotland") {
      const f = SCOTTISH_FLAT[flatKey];
      return withNotes({ kind: "flat", region, allowance: 0, flatRate: f.rate, flatLabel: f.label, description: `S${flatKey}: all pay at the ${f.label} (${f.rate}%)` });
    }
    const f = REST_FLAT[flatKey];
    if (!f) return bad(`${flatKey} is only used with the S prefix (Scottish advanced and top rates are SD2 and SD3).`);
    return withNotes({ kind: "flat", region, allowance: 0, flatRate: f.rate, flatLabel: f.label, description: `${region === "wales" ? "C" : ""}${flatKey}: all pay at the ${f.label} (${f.rate}%)` });
  }
  const k = /^K(\d{1,5})$/.exec(code);
  if (k) {
    const n = Number(k[1]);
    return withNotes({ kind: "k", region, allowance: -n * 10, letter: "K", description: `K${n}: ${(n * 10).toLocaleString("en-GB")} pounds is added to your pay before tax (deductions for benefits or tax owed exceed your allowance)` });
  }
  const std = /^(\d{1,5})([LMNT])$/.exec(code);
  if (std) {
    const n = Number(std[1]);
    const meaning: Record<string, string> = { L: "standard personal allowance", M: "includes Marriage Allowance received", N: "includes Marriage Allowance given away", T: "allowance under review by HMRC" };
    return withNotes({ kind: "standard", region, allowance: n * 10, letter: std[2], description: `${regionName ? regionName + " " : ""}${n}${std[2]}: allowance of ${(n * 10).toLocaleString("en-GB")} pounds (${meaning[std[2]]})` });
  }
  return bad(`"${raw}" is not a tax code this tool understands. Examples: 1257L, 1383M, 1131N, 0T, BR, D0, D1, K475, S1257L, C1257L.`);
}
